import React, { useState, useRef } from 'react';
import classNames from 'classnames/bind';
import { useDispatch } from 'react-redux';
import useMountEffect from '@restart/hooks/useMountEffect';

import { getRuntimeUsers } from '../constants/runtimeConfig';
import styles from './Form.module.scss';
import actions from '../actions';

const cx = classNames.bind(styles);

const Form = () => {
  const dispatch = useDispatch();
  const [account, setAccount] = useState({
    id: '',
    password: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setProcessing] = useState(false);
  const idRef = useRef();
  const passwordRef = useRef();

  useMountEffect(() => {
    idRef.current.focus();
  });

  const handleChange = (e) => {
    const fieldName = e.target.dataset.accountField || e.target.name;

    setAccount({
      ...account,
      [fieldName]: e.target.value,
    });
    setErrorMessage('');
  };

  const pressEnter = (e) => {
    if (e.key === 'Enter') {
      const fieldName = e.target.dataset.accountField || e.target.name;

      switch (fieldName) {
        case 'id':
          passwordRef.current.focus();
          break;

        case 'password':
          doLogin();
          break;

        default:
          break;
      }
    }
  };

  // 유효성 검사
  const validate = () => {
    if (account.id.trim().length === 0) {
      setErrorMessage('아이디를 입력해 주세요.');
      idRef.current.focus();
      return false;
    }
    if (account.password.trim().length === 0) {
      setErrorMessage('비밀번호를 입력해 주세요.');
      passwordRef.current.focus();
      return false;
    }
    return true;
  };

  // 로그인
  const doLogin = () => {
    if (isProcessing) return;
    if (!validate()) return;

    const configuredUsers = Object.values(getRuntimeUsers()).filter((user) => user.id && user.password && user.key && user.info);

    if (configuredUsers.length === 0) {
      setErrorMessage('로그인 설정이 비어 있습니다. 운영자에게 문의해 주세요.');
      setProcessing(false);
      return;
    }

    setErrorMessage('');
    setProcessing(true);

    const matchedUser = configuredUsers.find((user) => account.id === user.id && account.password === user.password);

    setTimeout(() => {
      if (!matchedUser) {
        setProcessing(false);
        setErrorMessage('아이디 또는 비밀번호를 다시 확인해 주세요.');
        return;
      }

      localStorage.setItem(
        '@ims/isLoggedInfo',
        JSON.stringify({
          id: matchedUser.id,
          ...matchedUser.info,
          key: matchedUser.key,
          tmsUserRid: matchedUser.tmsUserRid ?? null,
        }),
      );
      setProcessing(false);
      dispatch(
        actions.auth.login(
          matchedUser.id,
          matchedUser.info.role,
          matchedUser.info.menus,
          matchedUser.key,
          matchedUser.tmsUserRid ?? null,
        ),
      );
    }, 1000);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('logo')} />
      <div className={cx('fieldsWrapper')}>
        <input
          name="ims-login-id"
          data-account-field="id"
          type="text"
          ref={idRef}
          value={account.id}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck="false"
          disabled={isProcessing}
          onChange={handleChange}
          onKeyDown={pressEnter}
          placeholder="ID"
          className={cx('field')}
        />
        <input
          name="ims-login-password"
          data-account-field="password"
          type="password"
          ref={passwordRef}
          value={account.password}
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck="false"
          disabled={isProcessing}
          onChange={handleChange}
          onKeyDown={pressEnter}
          placeholder="Password"
          className={cx('field')}
        />
      </div>
      <div className={cx('message')}>{errorMessage}</div>
      <div className={cx(['button', { isProcessing }])} onClick={doLogin}>
        로그인
        <div className={cx('spinner')} />
      </div>
    </div>
  );
};

export default Form;
